/**
 * Interview Assessment API
 * Endpoints for managing realtime interview assessments
 */

import apiClient from "../apiClient";
import {
  AssessmentPayload,
  AssessmentSubmissionResponse,
  PollStatusResponse,
  AssessmentFeedbackResponse,
} from "@/types/interview-feedback.types";
import { logger } from "@/utils/logger";

/**
 * Submit assessment after realtime interview
 * POST /teacher/realtime/assessment
 */
export const submitAssessment = async (
  payload: AssessmentPayload
): Promise<AssessmentSubmissionResponse> => {
  // Log token status for debugging
  const token = localStorage.getItem("session_token");
  logger.log("[API] Submitting assessment:", {
    conversation_id: payload.conversation_id,
    question_id: payload.question_id,
    transcript_length: payload.transcript.length,
    has_token: !!token,
    token_preview: token ? `${token.substring(0, 20)}...` : "NO TOKEN",
  });

  const response = await apiClient.post<AssessmentSubmissionResponse>(
    "/teacher/realtime/assessment",
    payload
  );

  logger.log("[API] Assessment submitted:", {
    assessment_id: response.assessment_id,
    status: response.status,
  });

  return response;
};

/**
 * Poll assessment processing status
 * GET /teacher/realtime/assessment/status/:id?meeting_session={uuid}
 */
export const pollAssessmentStatus = async (
  assessmentId: string,
  meetingSession: string
): Promise<PollStatusResponse> => {
  const response = await apiClient.get<PollStatusResponse>(
    `/teacher/realtime/assessment/status/${assessmentId}?meeting_session=${meetingSession}`
  );

  return response;
};

/**
 * Get complete assessment feedback
 * GET /teacher/realtime/assessment/long/:id
 */
export const getAssessmentFeedback = async (
  assessmentId: string
): Promise<AssessmentFeedbackResponse> => {
  logger.log("[API] Fetching assessment feedback:", assessmentId);

  const response = await apiClient.get<AssessmentFeedbackResponse>(
    `/teacher/realtime/assessment/long/${assessmentId}`
  );

  logger.log("[API] Assessment feedback received:", {
    assessment_id: response.assessment_id,
    blocks_count: response.interviewPracticeFeedback.blocks.length,
  });

  return response;
};
